<script setup lang="ts">
import { type ComputedRef, computed, ref } from 'vue'

import FormInput from '@tix/ds/components/atoms/FormInput.vue'
import type { FormInputExposed } from '@tix/ds/components/atoms/form-input/types.ts'

import { mapIdentityFieldsToFormInput } from '@/helpers/identityForm/identityFieldMapping'
import { type GetIdentityFormFieldsOptions, getIdentityFormFields } from '@/helpers/identityForm/identityFormHelpers'
import type { IdentityFormData, IdentityFormDataValue } from '@/helpers/identityForm/identityHelpers'
import { customFieldsDefaults, isGiftAidFieldKey } from '@/helpers/identityForm/identityHelpers'

import GiftAidField from '@/components/donations/GiftAidField.vue'

type ConfirmFieldRule = {
  sourceKey: string
  caseInsensitive: boolean
  message: string
}

/** Matches legacy web-client `mustEqual` / `mustEqualCaseInsensitive` for confirm fields. */
const CONFIRM_FIELD_RULES: Record<string, ConfirmFieldRule> = {
  email_confirm: {
    sourceKey: 'email',
    caseInsensitive: true,
    message: 'Email addresses must match.',
  },
  password_confirm: {
    sourceKey: 'password',
    caseInsensitive: false,
    message: 'Passwords must match.',
  },
}

export type IdentityFormValidateResult = { error?: string }

type Props = {
  fieldOptions?: GetIdentityFormFieldsOptions
  heading?: string
  /**
   * Locks every input so values cannot change mid-checkout.
   * Implemented by passing `disabled` to FormInput only. Do not set `field.disabled` to the individual fields — that means "omit this answer" and `finalFormData` would drop email/name before resolveIdentityData() runs.
   */
  disabled?: boolean
}

const props = defineProps<Props>()

const defaults = customFieldsDefaults()
const formData = ref<IdentityFormData>(customFieldsDefaults())
const inputRefs = ref<Record<string, FormInputExposed | null>>({})

const sectionHeading = computed(() => {
  return props.heading ?? 'Your information'
})

const fields = computed(() => getIdentityFormFields(formData.value, [], props.fieldOptions))

const formInputs = computed(() => mapIdentityFieldsToFormInput(fields.value, formData.value))

const resolvedFormData: ComputedRef<IdentityFormData> = computed(() => {
  const next: IdentityFormData = {}

  for (const field of fields.value) {
    const existing = formData.value[field.key] ?? defaults[field.key]

    if (existing === undefined) continue

    const hasValidOptionValue = field.options?.some((option) => option.value === existing) ?? true

    if (
      // Don't set answers for disabled fields unless they are read-only.
      (field.disabled && !field.readOnly) ||
      !hasValidOptionValue
    )
      continue

    next[field.key] = normalizeFieldValue(field.type, existing)
  }

  return next
})

function normalizeFieldValue(type: string, value: IdentityFormDataValue): IdentityFormDataValue {
  if (type === 'checkbox' && typeof value === 'string') {
    return value === '1' || value === 'true'
  }
  return value
}

function onFieldUpdate(key: string, value: IdentityFormDataValue | undefined) {
  if (value === undefined) {
    const next = { ...formData.value }
    delete next[key]
    formData.value = next
    return
  }

  formData.value = { ...formData.value, [key]: value }
}

function setInputRef(key: string, el: unknown) {
  inputRefs.value[key] = (el as FormInputExposed | null) ?? null
}

// TODO Move to DS FormInput
function confirmFieldError(key: string): string | undefined {
  const rule = CONFIRM_FIELD_RULES[key]
  if (!rule) return undefined

  const confirm = String(formData.value[key] ?? '')
  if (!confirm) return undefined

  const source = String(formData.value[rule.sourceKey] ?? '')
  const matches = rule.caseInsensitive ? source.toLowerCase() === confirm.toLowerCase() : source === confirm

  return matches ? undefined : rule.message
}

const finalFormData = computed(() => {
  const answers: IdentityFormData = {}

  for (const field of fields.value) {
    const ignoredFields = ['email_confirm', 'password_confirm']
    if (field.disabled || field.readOnly || ignoredFields.includes(field.key)) continue

    const value = formData.value[field.key]
    if (value !== undefined) {
      answers[field.key] = value
    }
  }

  return answers
})

function validate(): IdentityFormValidateResult {
  let countOfErrors = 0

  for (const field of formInputs.value) {
    if (isGiftAidFieldKey(field.id)) continue
    const error = inputRefs.value[field.id]?.validateAndReturnError()
    if (error || confirmFieldError(field.id)) countOfErrors++
  }

  if (countOfErrors === 0) return {}

  return {
    error: countOfErrors === 1 ? '1 field needs your attention.' : `${countOfErrors} fields need your attention.`,
  }
}

defineExpose({ finalFormData, validate })
</script>

<template>
  <section class="identity-form">
    <h2 v-if="sectionHeading">{{ sectionHeading }}</h2>

    <div class="identity-form-fields">
      <template v-for="field in formInputs" :key="field.id">
        <GiftAidField
          v-if="isGiftAidFieldKey(field.id)"
          :id="field.id"
          v-model="formData[field.id] as boolean"
          :disabled="disabled"
          :label="field.label ?? ''"
        />
        <FormInput
          v-else
          :ref="(el) => setInputRef(field.id, el)"
          v-bind="field"
          :disabled="disabled || field.disabled"
          :error="confirmFieldError(field.id)"
          :modelValue="resolvedFormData[field.id]"
          @update:modelValue="onFieldUpdate(field.id, $event)"
        />
      </template>
    </div>
  </section>
</template>

<style scoped>
.identity-form {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);

  .identity-form-fields {
    display: flex;
    flex-direction: column;
    gap: var(--space-1);
  }
}
</style>
